import { AlertTriangle, ArrowLeft, LayoutList, Network, Play, Save, Undo2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button, ConfirmDialog, cx, ErrorNote, Modal, Spinner, toast } from "../../../components/ui";
import { api, ApiError } from "../../../lib/api";
import { allSteps, branchById, branchOfStep, findStep, newBranchId, newTreeStepId, updateStep, type Definition, type Issue, type Step } from "./model";
import { BranchPanel, TreeCanvas, TreeList, type TreeHandlers } from "./TreeView";
import { PromptsEditor } from "./PromptsEditor";
import { StepEditor } from "./StepEditor";
import { TemplatesEditor } from "./TemplatesEditor";
import { FunctionsEditor } from "./FunctionsEditor";
import { ArraysEditor } from "./ArraysEditor";
import { VersionsPanel, type VersionInfo } from "./VersionsPanel";

type Saving = "saved" | "dirty" | "saving" | "conflict" | "error";

/**
 * The Designer (docs/phase3-plan.md): the trunk as a canvas (steps as cards down the centre; branches come next
 * phase) or as a list, with the selected step's editor beside it. Edits save to the working draft as you go;
 * "Save version" validates and makes the next version, which is what runs use.
 */
export default function JaDesigner() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [def, setDef] = useState<Definition | null>(null);
  const [rev, setRev] = useState(0);
  const [current, setCurrent] = useState(0);
  const [hasDraft, setHasDraft] = useState(false);
  // The version the draft started from: a version saved or imported since makes it stale.
  const [base, setBase] = useState<number | null>(null);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<Saving>("saved");
  const [selected, setSelected] = useState<string | null>(null);
  const [area, setArea] = useState<"steps" | "prompts" | "templates" | "functions" | "arrays" | "versions">("steps");
  const [versions, setVersions] = useState<VersionInfo[]>([]);
  const [view, setView] = useState<"canvas" | "list">(() => (localStorageGet("ja.designer.view") === "list" ? "list" : "canvas"));
  const [versionOpen, setVersionOpen] = useState(false);
  const [discard, setDiscard] = useState(false);
  const pending = useRef<Definition | null>(null);
  const revRef = useRef(0);

  const load = useCallback(async () => {
    try {
      const d = await api<{ definition: Definition; rev: number; draft: { by: string; at: string; base: number } | null; current: number; versions: VersionInfo[]; issues: Issue[] }>(`/apps/ja/automations/${id}/draft`);
      setDef(d.definition);
      setRev(d.rev);
      revRef.current = d.rev;
      setCurrent(d.current);
      setVersions(d.versions ?? []);
      setHasDraft(!!d.draft);
      setBase(d.draft?.base ?? null);
      setIssues(d.issues);
      setSaving("saved");
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [id]);
  useEffect(() => {
    void load();
  }, [load]);

  // Saves the draft a moment after the last edit (one save in flight at a time).
  const flush = useCallback(async () => {
    const d = pending.current;
    if (!d) return;
    pending.current = null;
    setSaving("saving");
    try {
      const r = await api<{ rev: number; issues: Issue[] }>(`/apps/ja/automations/${id}/draft`, { method: "PUT", body: { definition: d, rev: revRef.current } });
      revRef.current = r.rev;
      setRev(r.rev);
      setIssues(r.issues);
      setHasDraft(true);
      setSaving(pending.current ? "dirty" : "saved");
    } catch (e) {
      setSaving(e instanceof ApiError && e.status === 409 ? "conflict" : "error");
      if (!(e instanceof ApiError && e.status === 409)) toast((e as Error).message, "error");
    }
  }, [id]);
  useEffect(() => {
    if (saving !== "dirty") return;
    const t = setTimeout(() => void flush(), 1200);
    return () => clearTimeout(t);
  }, [saving, flush, def]);

  const change = (next: Definition) => {
    setDef(next);
    pending.current = next;
    if (saving !== "conflict") setSaving("dirty");
  };
  const setStep = (s: Step) => def && change(updateStep(def, s));
  const renumber = (steps: Step[]) => steps.map((s, i) => ({ ...s, seq: i + 1 }));
  /** The list a step is in (the trunk or a step branch's steps), and how to put it back. */
  const listOf = (d: Definition, stepId: string | undefined, branch?: string): { steps: Step[]; put: (steps: Step[]) => Definition } => {
    const b = branch ? branchById(d, branch) : stepId ? branchOfStep(d, stepId) : undefined;
    if (!b) return { steps: d.trunk, put: (steps) => ({ ...d, trunk: renumber(steps) }) };
    return { steps: b.steps, put: (steps) => ({ ...d, branches: (d.branches ?? []).map((x) => (x.id === b.id ? { ...x, steps: renumber(steps) } : x)) }) };
  };
  const blank = (d: Definition, at: number): Step => ({ id: newTreeStepId(d), seq: at + 1, name: "New step", kind: "job", verify: { method: "manual" }, pollSeconds: 15, maxChecks: 960, breakAfter: false, reports: [], archives: [], rerun: true, infor: { class: "", action: "", submit: "direct", fields: [] } });
  const addStep = (at: number, branch?: string) => {
    if (!def) return;
    const s = blank(def, at);
    const { steps, put } = listOf(def, undefined, branch);
    const next = [...steps];
    next.splice(at, 0, s);
    change(put(next));
    setSelected(s.id);
  };
  /** A new step branch started by this step, with one new step in it. */
  const addBranch = (stepId: string) => {
    if (!def) return;
    const id = newBranchId(def);
    const first = blank(def, 0);
    const starter = findStep(def, stepId)!;
    const d = updateStep({ ...def, branches: [...(def.branches ?? []), { id, name: `Branch ${(def.branches?.length ?? 0) + 1}`, kind: "steps", critical: false, steps: [first] }] }, { ...starter, branches: [...(starter.branches ?? []), id] });
    change(d);
    setSelected(first.id);
  };
  /** Removes a step branch, its steps, and the branches they start. */
  const dropBranch = (d: Definition, id: string): Definition => {
    const b = branchById(d, id);
    let out: Definition = { ...d, branches: (d.branches ?? []).filter((x) => x.id !== id) };
    for (const s of allSteps(out)) if (s.branches?.includes(id)) out = updateStep(out, { ...s, branches: s.branches.filter((x) => x !== id) });
    for (const st of b?.steps ?? []) for (const inner of st.branches ?? []) if (branchById(out, inner)) out = dropBranch(out, inner);
    return out;
  };
  const move = (dir: -1 | 1) => {
    if (!def || !selected) return;
    const { steps, put } = listOf(def, selected);
    const i = steps.findIndex((s) => s.id === selected);
    const j = i + dir;
    if (j < 0 || j >= steps.length) return;
    const next = [...steps];
    [next[i], next[j]] = [next[j], next[i]];
    change(put(next));
  };
  const copy = () => {
    if (!def || !selected) return;
    const { steps, put } = listOf(def, selected);
    const i = steps.findIndex((s) => s.id === selected);
    // The copy starts no branches of its own (a branch has one starter).
    const s = { ...structuredClone(steps[i]), id: newTreeStepId(def), name: `${steps[i].name} (copy)`, branches: undefined };
    const next = [...steps];
    next.splice(i + 1, 0, s);
    change(put(next));
    setSelected(s.id);
  };
  const remove = () => {
    if (!def || !selected) return;
    const gone = findStep(def, selected);
    let d = def;
    for (const b of gone?.branches ?? []) if (branchById(d, b)) d = dropBranch(d, b);
    const { steps, put } = listOf(d, selected);
    change(put(steps.filter((s) => s.id !== selected)));
    setSelected(null);
  };

  if (error && !def) return <ErrorNote>{error}</ErrorNote>;
  if (!def) return <Spinner />;
  const step = selected && !selected.startsWith("branch:") ? findStep(def, selected) : undefined;
  const branch = selected?.startsWith("branch:") ? branchById(def, selected.slice(7)) : undefined;
  const errors = issues.filter((i) => i.level === "error");
  const warns = issues.filter((i) => i.level === "warn");
  const stepIssues = (s: Step) => issues.filter((i) => i.where === `step ${s.id}`);
  const tree: TreeHandlers = { selected, onSelect: setSelected, onAdd: addStep, onAddBranch: addBranch, issuesOf: stepIssues, branchIssues: (b) => issues.filter((i) => i.where === `branch ${b.id}`) };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to={`/apps/ja/automations/${id}`} className="inline-flex items-center gap-1 text-sm text-accent-text hover:underline mb-2">
            <ArrowLeft className="size-4" aria-hidden /> {def.name}
          </Link>
          <h1 className="text-2xl font-bold text-ink">
            Designer <span className="text-muted font-normal text-base">· {def.type} · {hasDraft ? `draft of v${current}` : `v${current}`}</span>
          </h1>
          <p className="text-sm text-muted" aria-live="polite">
            {saving === "saving" ? "Saving…" : saving === "dirty" ? "Unsaved changes…" : saving === "conflict" ? "" : saving === "error" ? "Couldn't save the draft; your changes are still here." : hasDraft ? `Draft saved (r${rev}). Runs still use v${current} until you save a version.` : "No changes yet."}
          </p>
          {hasDraft && base !== null && base < current && (
            <p className="text-sm text-warn">
              This draft started from v{base}, and v{current} has been saved since. Saving the draft as a version replaces v{current}'s changes: compare them under Versions first.
            </p>
          )}
          {saving === "conflict" && (
            <p className="text-sm text-bad">
              Someone else (or another window) changed this draft.{" "}
              <button type="button" className="underline" onClick={() => void load()}>
                Reload their version
              </button>{" "}
              (your unsaved edits here will be lost).
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="inline-flex rounded-lg border border-line overflow-hidden" role="group" aria-label="View">
            {(["canvas", "list"] as const).map((v) => (
              <button
                key={v}
                type="button"
                aria-pressed={view === v}
                onClick={() => {
                  setView(v);
                  localStorageSet("ja.designer.view", v);
                }}
                className={cx("inline-flex items-center gap-1 px-3 h-9 text-sm", view === v ? "bg-accent-soft text-accent-text font-medium" : "bg-surface text-ink-2 hover:bg-surface-2")}
              >
                {v === "canvas" ? <Network className="size-4" aria-hidden /> : <LayoutList className="size-4" aria-hidden />}
                {v === "canvas" ? "Canvas" : "List"}
              </button>
            ))}
          </div>
          {hasDraft && (
            <Button onClick={() => navigate(`/apps/ja/automations/${id}/run?draft=1`)} title="Test-run this draft on a non-production connection">
              <Play className="size-4" aria-hidden /> Test-run draft
            </Button>
          )}
          {hasDraft && (
            <Button onClick={() => setDiscard(true)}>
              <Undo2 className="size-4" aria-hidden /> Discard draft
            </Button>
          )}
          <Button variant="primary" disabled={!hasDraft || saving !== "saved" || errors.length > 0} onClick={() => setVersionOpen(true)} title={errors.length ? "Fix the problems first" : undefined}>
            <Save className="size-4" aria-hidden /> Save version {current + 1}
          </Button>
        </div>
      </div>

      {(errors.length > 0 || warns.length > 0) && (
        <details className={cx("rounded-xl border p-3 text-sm", errors.length ? "border-bad bg-bad-soft" : "border-warn bg-warn-soft")} open={errors.length > 0}>
          <summary className="cursor-pointer font-medium text-ink">
            {errors.length > 0 && `${errors.length} to fix before saving`}
            {errors.length > 0 && warns.length > 0 && " · "}
            {warns.length > 0 && `${warns.length} warning${warns.length === 1 ? "" : "s"} from Waypoint and the checks`}
          </summary>
          <ul className="mt-2 space-y-1">
            {issues.map((i, k) => {
              const sid = /^step (\w+)/.exec(i.where)?.[1];
              const other = /^(prompt|template|run key|function|array)/.exec(i.where)?.[1];
              return (
                <li key={k} className="flex gap-2">
                  <span className={cx("text-xs font-semibold uppercase w-12 shrink-0 pt-0.5", i.level === "error" ? "text-bad" : "text-warn")}>{i.level === "error" ? "fix" : "warn"}</span>
                  {sid ? (
                    <button type="button" className="text-left hover:underline" onClick={() => (setArea("steps"), setSelected(sid))}>
                      <span className="font-mono text-xs text-muted">{i.where}</span> {i.message}
                    </button>
                  ) : other ? (
                    <button type="button" className="text-left hover:underline" onClick={() => setArea(other === "template" ? "templates" : other === "function" ? "functions" : other === "array" ? "arrays" : "prompts")}>
                      <span className="font-mono text-xs text-muted">{i.where}</span> {i.message}
                    </button>
                  ) : (
                    <span>
                      <span className="font-mono text-xs text-muted">{i.where}</span> {i.message}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </details>
      )}

      <div className="flex gap-1 border-b border-line" role="tablist" aria-label="Part of the design">
        {(
          [
            ["steps", `Steps (${def.trunk.length})`],
            ["prompts", `Prompts (${def.prompts.length})`],
            ["templates", `Report templates (${def.templates.length})`],
            ["functions", `Functions (${(def.functions ?? []).length})`],
            ["arrays", `Arrays (${(def.arrays ?? []).length})`],
            ["versions", `Versions (${versions.length})`],
          ] as const
        ).map(([a, label]) => {
          const bad = a !== "versions" && issues.some((i) => i.level === "error" && (a === "steps" ? i.where.startsWith("step") : a === "prompts" ? /^(prompt|run key)/.test(i.where) : a === "functions" ? i.where.startsWith("function") : a === "arrays" ? i.where.startsWith("array") : i.where.startsWith("template")));
          return (
            <button key={a} type="button" role="tab" aria-selected={area === a} onClick={() => setArea(a)} className={cx("px-3 h-9 text-sm -mb-px border-b-2", area === a ? "border-accent text-accent-text font-medium" : "border-transparent text-ink-2 hover:text-ink")}>
              {label}
              {bad && <AlertTriangle className="inline size-3.5 text-bad ml-1 -mt-0.5" aria-label="has problems" />}
            </button>
          );
        })}
      </div>

      {area === "prompts" && <PromptsEditor def={def} issues={issues} onChange={change} />}
      {area === "templates" && <TemplatesEditor def={def} issues={issues} onChange={change} />}
      {area === "arrays" && <ArraysEditor def={def} issues={issues} onChange={change} />}
      {area === "functions" && <FunctionsEditor id={id} def={def} issues={issues} onChange={change} />}
      {area === "versions" && <VersionsPanel id={id} versions={versions} current={current} hasDraft={hasDraft} rev={rev} onRestored={() => void load()} />}
      {area === "steps" && (
        <div className={cx("grid gap-4", step || branch ? "lg:grid-cols-[minmax(0,1fr)_28rem]" : "")}>
          <div>{view === "canvas" ? <TreeCanvas def={def} h={tree} /> : <TreeList def={def} h={tree} />}</div>
          {step && <StepEditor def={def} step={step} issues={issues} onChange={setStep} onMove={move} onCopy={copy} onDelete={remove} onClose={() => setSelected(null)} onAddBranch={() => addBranch(step.id)} onSelectBranch={(id) => setSelected(`branch:${id}`)} />}
          {branch && (
            <BranchPanel
              def={def}
              branch={branch}
              issues={issues.filter((i) => i.where === `branch ${branch.id}`)}
              onChange={(b) => change({ ...def, branches: (def.branches ?? []).map((x) => (x.id === b.id ? b : x)) })}
              onDelete={() => {
                change(dropBranch(def, branch.id));
                setSelected(null);
              }}
              onClose={() => setSelected(null)}
            />
          )}
        </div>
      )}

      {versionOpen && <SaveVersion id={id} rev={rev} next={current + 1} warnings={warns.length} onClose={() => setVersionOpen(false)} onSaved={() => (setVersionOpen(false), void load())} />}
      {discard && (
        <ConfirmDialog
          title="Discard the draft?"
          confirm="Discard"
          danger
          onConfirm={async () => {
            await api(`/apps/ja/automations/${id}/draft?rev=${revRef.current}`, { method: "DELETE" });
            toast("Draft discarded");
            setSelected(null);
            await load();
          }}
          onClose={() => setDiscard(false)}
        >
          <p className="text-sm text-ink-2">Your changes since v{current} are thrown away. v{current} stays as it is.</p>
        </ConfirmDialog>
      )}
    </div>
  );
}

function SaveVersion({ id, rev, next, warnings, onClose, onSaved }: { id: string; rev: number; next: number; warnings: number; onClose: () => void; onSaved: () => void }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <Modal
      title={`Save version ${next}`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            busy={busy}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await api(`/apps/ja/automations/${id}/versions`, { body: { rev, note } });
                toast(`Saved version ${next}. New runs use it.`);
                onSaved();
              } catch (e) {
                setError((e as Error).message);
                setBusy(false);
              }
            }}
          >
            Save version {next}
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        {error && <ErrorNote>{error}</ErrorNote>}
        <p className="text-ink-2">New runs will use version {next}. Runs already started keep the version they started with.</p>
        {warnings > 0 && <p className="text-warn">{warnings} warning{warnings === 1 ? "" : "s"} will be saved with it.</p>}
        <label className="block">
          <span className="text-ink-2 font-medium">What changed</span>
          <input className="w-full h-9 rounded-lg border border-line bg-surface px-3 text-sm" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Added the Current Time report" maxLength={300} />
        </label>
      </div>
    </Modal>
  );
}

function localStorageGet(k: string) {
  try {
    return window.localStorage.getItem(k);
  } catch {
    return null;
  }
}
function localStorageSet(k: string, v: string) {
  try {
    window.localStorage.setItem(k, v);
  } catch {
    /* private window: the choice just isn't remembered */
  }
}
